import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach Bearer token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Try to refresh token
      try {
        const token = localStorage.getItem('access_token');
        await axios.post(`${API_URL}/api/auth/refresh`, {}, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        // Retry the original request
        return api.request(error.config);
      } catch {
        // Clear token and redirect to login
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboard = () => api.get('/dashboard');

// Channels
export const getChannels = () => api.get('/channels');
export const getChannel = (id) => api.get(`/channels/${id}`);
export const createChannel = (data) => api.post('/channels', data);
export const updateChannel = (id, data) => api.put(`/channels/${id}`, data);
export const deleteChannel = (id) => api.delete(`/channels/${id}`);

// Videos
export const getVideos = (params) => api.get('/videos', { params });
export const getVideo = (id) => api.get(`/videos/${id}`);
export const createVideo = (data) => api.post('/videos', data);
export const updateVideo = (id, data) => api.put(`/videos/${id}`, data);
export const deleteVideo = (id) => api.delete(`/videos/${id}`);
export const bulkUpdateVideos = (data) => api.post('/bulk/videos', data);
export const bulkDeleteVideos = (data) => api.post('/bulk/videos/delete', data);

// Editors
export const getEditors = () => api.get('/editors');
export const getEditor = (id) => api.get(`/editors/${id}`);
export const createEditor = (data) => api.post('/editors', data);
export const updateEditor = (id, data) => api.put(`/editors/${id}`, data);
export const deleteEditor = (id) => api.delete(`/editors/${id}`);
export const getEditorAssignments = (id) => api.get(`/editors/${id}/assignments`);

// Concepts
export const getConcepts = () => api.get('/concepts');
export const getConcept = (id) => api.get(`/concepts/${id}`);
export const createConcept = (data) => api.post('/concepts', data);
export const updateConcept = (id, data) => api.put(`/concepts/${id}`, data);
export const deleteConcept = (id) => api.delete(`/concepts/${id}`);
export const launchConcept = (id) => api.post(`/concepts/${id}/launch`);

// Ideas
export const getIdeas = (params) => api.get('/ideas', { params });
export const createIdea = (data) => api.post('/ideas', data);
export const updateIdea = (id, data) => api.put(`/ideas/${id}`, data);
export const deleteIdea = (id) => api.delete(`/ideas/${id}`);
export const convertIdea = (id) => api.post(`/ideas/${id}/convert`);

// Reports
export const getSummaryReport = (period) => api.get('/reports/summary', { params: { period } });
export const getPipelineReport = () => api.get('/reports/pipeline');
export const getChannelsReport = () => api.get('/reports/channels');
export const getEditorsReport = () => api.get('/reports/editors');
export const getGapsReport = () => api.get('/reports/gaps');
export const getEditorPaymentReport = () => api.get('/reports/editors-payment');

// Completed videos
export const getCompletedVideos = () => api.get('/completed/videos');
export const moveToCompleted = (data) => api.post('/completed/videos', data);

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users/create', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const inviteUser = (data) => api.post('/users/invite', data);
export const getUserActivity = (id) => api.get(`/users/${id}/activity`);

// Invitations
export const getInvitations = () => api.get('/invitations');
export const resendInvitation = (id) => api.post(`/invitations/${id}/resend`);
export const revokeInvitation = (id) => api.delete(`/invitations/${id}`);

// Import/Export
export const importVideos = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/videos', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const exportVideos = (params) => 
  api.get('/export/videos', { 
    params, 
    responseType: 'blob' 
  });

export const downloadTemplate = (type) => 
  api.get(`/export/templates/${type}`, { 
    responseType: 'blob' 
  });

// AI
export const getAISuggestions = (prompt, context) => 
  api.post('/ai/suggestions', { prompt, context });

export const getContentGaps = () => api.get('/ai/content-gaps');

// Search
export const globalSearch = (q) => api.get('/search', { params: { q } });

// Activity
export const getActivity = (limit = 50) => api.get('/activity', { params: { limit } });

// Stage History
export const getStageHistory = (videoId) => api.get(`/videos/${videoId}/stage-history`);

// Shorts
export const getShorts = (params) => api.get('/shorts', { params });
export const createShort = (data) => api.post('/shorts', data);
export const updateShort = (id, data) => api.put(`/shorts/${id}`, data);
export const deleteShort = (id) => api.delete(`/shorts/${id}`);

// Script Upload
export const uploadScript = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/script', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Google Drive
export const getDriveAuthUrl = () => api.get('/oauth/drive/auth');
export const getDriveStatus = () => api.get('/drive/status');
export const uploadToDrive = (file, folderId) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = folderId ? { folder_id: folderId } : {};
  return api.post('/upload/drive-video', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params,
  });
};

// Comments
export const getVideoComments = (videoId) => api.get(`/videos/${videoId}/comments`);
export const addVideoComment = (videoId, text) => api.post(`/videos/${videoId}/comments`, { text });
export const getLatestComments = () => api.get('/comments/latest');


export default api;
